import ArrowDown from '@/app/icons/ArrowDown/ArrowDown'
import React, { ComponentProps, useRef, useState } from 'react'

export type AccordionProps = Omit<ComponentProps<'div'>, 'title'> & {
  title: React.ReactNode | ((isOpen: boolean) => React.ReactNode)
  children: React.ReactNode
  icon?: React.ReactNode | null
  htmlTitle?: string
  open?: boolean
  disabled?: boolean
  buttonPosition?: 'top' | 'bottom'
}

export function Accordion({
  title,
  children,
  icon,
  htmlTitle,
  open = false,
  disabled = false,
  buttonPosition = 'top',
  ...props
}: AccordionProps) {
  const [isOpen, setIsOpen] = useState(open)
  const contentRef = useRef<HTMLDivElement>(null)
  const DefaultIcon = ArrowDown

  const toggleAccordion = () => {
    setIsOpen((prev) => !prev)
  }

  const ButtonElement = (
    <button
      disabled={disabled}
      onClick={toggleAccordion}
      type="button"
      className="flex w-full cursor-pointer items-start justify-between gap-2 text-left font-title text-base text-mydra-black focus:outline-none md:text-2xl"
    >
      {typeof title === 'function' ? title(isOpen) : title}
      {icon !== null && (
        <div
          className={`text-mydra-black transition-all duration-300 ease-in-out ${isOpen ? '' : 'rotate-180'}`}
        >
          {icon ? (
            React.cloneElement(icon as React.ReactElement, {
              className: 'h-4 w-4 md:h-5 md:w-5',
            })
          ) : (
            <DefaultIcon className="h-4 w-4 md:h-5 md:w-5" />
          )}
        </div>
      )}
    </button>
  )

  return (
    <div
      {...props}
      className={`overflow-hidden ${props.className ?? ''}`}
      title={htmlTitle}
    >
      {buttonPosition === 'top' && ButtonElement}
      <div
        ref={contentRef}
        className={`overflow-hidden transition-all duration-300 ease-in-out`}
        style={{
          height: isOpen ? contentRef.current?.scrollHeight + 'px' : '0px',
        }}
      >
        <div className="max-w-prose text-base md:pt-0">{children}</div>
      </div>
      {buttonPosition === 'bottom' && ButtonElement}
    </div>
  )
}
